import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { createLease, attachDocumentsToLease, sendSigningLinksToTenants, updateLeaseStatuses, validateStep } from '../utils';

const STEP_KEYS = ['step1', 'step2', 'step3', 'step4', 'step5'];

export const useLeaseStepSubmission = ({
    validators,
    setIsSubmitting = () => { },
    setError,
    handleNext,
    leaseId,
}) => {
    const { property_id, unit_no, building_name } = useParams();
    const leaseStatuses = useSelector((state) => state.leaseStatus?.leaseStatuses || {});
    const history = useHistory();

    const handleSuccess = useCallback((message) => {
        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: message,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK',
        });
        return true;
    }, []);

    const handleStep5Success = useCallback((message) => {
        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: message,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK',
        }).then((result) => {
            if (result.isConfirmed) {
                history.push('/master_sheet');
            }
        });
        return true;
    }, [history]);

    const handleError = useCallback(
        (errorMessage, response) => {
            const message = response?.data?.message || errorMessage;
            setError(message);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message,
                confirmButtonColor: '#d33',
                confirmButtonText: 'OK',
            });
            return false;
        },
        [setError]
    );

    const submitHandlers = useMemo(
        () => ({
            step1: async (values) => {
                try {
                    if (!(await validateStep(validators.step1, values))) return false;

                    setIsSubmitting(true);
                    const response = await createLease({
                        property_id,
                        building_name,
                        unit_no,
                        payload: values,
                    });

                    return response.success
                        ? handleSuccess('Step 1 submitted successfully!')
                        : handleError('Failed to submit step 1', response);
                } catch (err) {
                    return handleError(err.message, err.response);
                } finally {
                    setIsSubmitting(false);
                }
            },

            step2: async (values) => {
                try {
                    if (!(await validateStep(validators.step2, values))) return false;

                    setIsSubmitting(true);
                    const response = await attachDocumentsToLease(leaseId, values);

                    return response.success
                        ? handleSuccess('Property Forms attached successfully!')
                        : handleError('Failed to attach property forms', response);
                } catch (err) {
                    return handleError(`Error submitting step 2: ${err.message}`, err.response);
                } finally {
                    setIsSubmitting(false);
                }
            },

            step3: async (values) => {
                try {
                    const inputFields = (values.tenants || []).reduce((acc, tenant, index) => ({
                        ...acc,
                        [`email_${index}`]: {
                            type: 'email',
                            label: `Email for ${tenant.name}`,
                            placeholder: `Enter email for ${tenant.name}`,
                            value: tenant.email || '',
                            required: true,
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: `Invalid email for ${tenant.name}`
                            }
                        }
                    }), {});

                    const { value: emailInputs } = await Swal.fire({
                        title: 'Enter Tenant Emails',
                        html: (values.tenants || [])
                            .map((tenant, index) => `
                    <div style="margin-bottom: 15px;">
                        <label style="display: block; margin-bottom: 5px;">
                            Email for ${tenant.name}
                        </label>
                        <input 
                            type="email" 
                            id="email_${index}" 
                            name="email_${index}" 
                            placeholder="Enter email address" 
                            value="${tenant.email || ''}"
                            style="width: 100%; padding: 8px; border-radius: 4px; border: 1px solid #ccc;"
                            required
                        >
                    </div>
                `).join(''),
                        focusConfirm: false,
                        showCancelButton: true,
                        confirmButtonText: 'Submit Emails',
                        preConfirm: () => {
                            const emails = {};
                            (values.tenants || []).forEach((_, index) => {
                                const input = document.getElementById(`email_${index}`);
                                emails[`email_${index}`] = input.value;
                            });
                            for (let i = 0; i < (values.tenants || []).length; i++) {
                                const email = emails[`email_${i}`];
                                if (!email) {
                                    Swal.showValidationMessage(`Email for ${values.tenants[i].name} is required`);
                                    return false;
                                }
                                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                                    Swal.showValidationMessage(`Invalid email for ${values.tenants[i].name}`);
                                    return false;
                                }
                            }
                            return emails;
                        }
                    });

                    if (!emailInputs) return false;

                    const tenantsWithEmails = (values.tenants || []).map((tenant, index) => ({
                        ...tenant,
                        email: emailInputs[`email_${index}`]
                    }));

                    const updatedValues = { ...values, tenants: tenantsWithEmails };

                    if (!(await validateStep(validators.step3, updatedValues))) return false;

                    setIsSubmitting(true);
                    const response = await sendSigningLinksToTenants(leaseId, updatedValues);

                    return response.success
                        ? handleSuccess('Lease Sent successfully!')
                        : handleError('Failed to send leases', response);
                } catch (err) {
                    return handleError(`Error submitting step 3: ${err.message}`, err.response);
                } finally {
                    setIsSubmitting(false);
                }
            },

            step4: async (values) => {
                try {
                    const validator = validators.step4.clone().context({ tenants: values.tenants });
                    if (!(await validateStep(validator, values))) return false;

                    setIsSubmitting(true);
                    const response = await axios.post('/api/lease/sign', values);
                    return response.data.success
                        ? handleSuccess('All signatures have been collected successfully!')
                        : handleError('Failed to sign lease', response);
                } catch (err) {
                    return handleError(`Error submitting step 4: ${err.message}`, err.response);
                } finally {
                    setIsSubmitting(false);
                }
            },

            step5: async (values) => {
                try {
                    if (!(await validateStep(validators.step5, values))) return false;

                    const confirm = await Swal.fire({
                        title: 'Are you sure?',
                        text: 'You are about to update lease statuses. This will activate one lease and update others.',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Yes, update',
                        cancelButtonText: 'Cancel',
                    });

                    if (!confirm.isConfirmed) return false;

                    Swal.fire({
                        title: 'Updating Statuses...',
                        text: 'Please wait while the lease statuses are being updated.',
                        allowOutsideClick: false,
                        showConfirmButton: false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    });

                    setIsSubmitting(true);

                    const active_lease_id = Object.keys(leaseStatuses).find(
                        (leaseId) => leaseStatuses[leaseId] === 'Active - New Lease' ||
                            leaseStatuses[leaseId] === 'Active - Renewal Lease' ||
                            leaseStatuses[leaseId] === 'Active' ||
                            leaseStatuses[leaseId] === 'Amendment'
                    );

                    if (!active_lease_id) {
                        Swal.close();
                        throw new Error('No lease selected as Active - New Lease, Active - Renewal Lease, Active, or Amendment');
                    }

                    const lease_type = leaseStatuses[active_lease_id] === 'Active - New Lease' 
                        ? 'NEW_LEASE' 
                        : leaseStatuses[active_lease_id] === 'Active - Renewal Lease' 
                        ? 'RENEWAL_LEASE' 
                        : 'AMENDMENT';

                    const other_lease_status = Object.entries(leaseStatuses)
                        .filter(([leaseId]) => leaseId !== active_lease_id)
                        .map(([leaseId, status]) => ({
                            lease_id: leaseId,
                            status,
                        }));

                    const response = await updateLeaseStatuses(
                        property_id,
                        building_name,
                        unit_no,
                        active_lease_id,
                        other_lease_status,
                        lease_type 
                    );

                    Swal.close();

                    if (response.success) {
                        return handleStep5Success('The lease has been successfully activated and statuses updated!');
                    } else {
                        return handleError('Failed to activate lease or update statuses', response);
                    }
                } catch (err) {
                    Swal.close();
                    return handleError(`Error submitting step 5: ${err.message}`, err.response);
                } finally {
                    setIsSubmitting(false);
                }
            }
        }),
        [
            property_id,
            unit_no,
            building_name,
            leaseId,
            leaseStatuses,
            setIsSubmitting,
            validators,
            handleSuccess,
            handleStep5Success,
            handleError,
        ]
    );

    const handleStepSubmission = useCallback(
        async (stepKey, values) => {
            if (!STEP_KEYS.includes(stepKey)) {
                setError(`Invalid step key: ${stepKey}`);
                return false;
            }

            const submitHandler = submitHandlers[stepKey];
            if (!submitHandler) {
                setError('No submit handler defined for this step');
                return false;
            }

            const success = await submitHandler(values);
            if (success && handleNext && stepKey !== 'step5') {
                handleNext();
            }
            return success;
        },
        [submitHandlers, handleNext, setError]
    );

    const getSubmitHandler = useCallback(
        (stepKey) => submitHandlers[stepKey] || null,
        [submitHandlers]
    );

    return {
        submitHandlers,
        handleStepSubmission,
        getSubmitHandler,
    };
};